import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Lock, Network, CheckCircle2,
  Stethoscope, Eye, Ear, Building2, Code2,
  Video, Users, Mail, FolderHeart, Calendar,
  Plug, Sparkles, FileCheck2, Workflow,
  UserRound, GraduationCap, BrainCircuit, Share2,
  HeartPulse, Server, Database, KeyRound,
  Zap, Timer, Palette, Globe, Layers, Wand2,
} from "lucide-react";
import SEO, {
  ORG_SCHEMA, WEBSITE_SCHEMA, softwareApplicationSchema,
  breadcrumb, faqSchema, MSSANTE_PERSONAS_ITEMLIST,
} from "../components/SEO";
import { QuickstartForm, HeroCounter } from "../components/landing/LandingEnhancers";

const HOME_FAQ = [
  {
    q: "Qu'est-ce que MetaConnect Studio ?",
    a: "MetaConnect Studio est le premier configurateur français qui permet de générer en moins de 5 minutes l'aperçu interactif de votre plateforme de télémédecine personnalisée — couleurs, logo, services (téléconsultation, téléexpertise, télésurveillance, messagerie MSSanté), agenda multi-praticiens et fiche vitrine référencée SEO. Vous repartez avec un cahier des charges PDF et un lien partageable à vos associés.",
  },
  {
    q: "Pourquoi c'est unique en France ?",
    a: "Aucun concurrent ne permet aujourd'hui à un professionnel de santé de visualiser, en 5 minutes chrono, sa future plateforme de télémédecine avec ses propres couleurs, son propre logo et ses propres workflows. Les éditeurs classiques imposent des devis longs, des démos commerciales, des cycles de 3 à 6 mois. MetaConnect Studio inverse le funnel : vous voyez le résultat avant de signer.",
  },
  {
    q: "Combien coûte une plateforme MetaConnect ?",
    a: "L'aperçu Studio et le cahier des charges PDF sont 100 % gratuits. Une fois votre projet validé, le déploiement de votre plateforme dépend du périmètre (modules choisis, volume de praticiens, intégrations métier). Tarification sur devis transparent post-cadrage : généralement entre 49 €/mois (cabinet libéral solo, MSSanté + agenda) et plusieurs centaines d'euros par mois pour les CPTS et grandes structures.",
  },
  {
    q: "Qui peut utiliser MetaConnect ?",
    a: "Deux profils : (1) les professionnels de santé et structures (cabinets, CPTS, MSP, magasins d'optique, audioprothèses) qui souhaitent leur propre plateforme de télémédecine en marque blanche ; (2) les éditeurs de logiciels santé qui veulent ajouter des briques télésanté (téléconsultation, téléexpertise, DMP, MSSanté, agenda) à leur produit existant.",
  },
  {
    q: "Comment se passe une intégration en marque blanche pour un éditeur de logiciel ?",
    a: "Échange initial de cadrage (faisabilité technique, environnement, logiciel métier, nombre de licences/postes, type d'identification PSC ou JWT) → maquette technique → intégration via API REST avec votre stack → recette → mise en production. La couche MetaConnect ne remplace pas votre logiciel : elle vient enrichir vos workflows existants.",
  },
  {
    q: "Quelles garanties de conformité offre MetaConnect ?",
    a: "Hébergement HDS (Hébergeur de Données de Santé certifié), conformité Référentiel #1 ANS / Espace de Confiance MSSanté, RGPD avec architecture sous-traitance documentée, ISO 27001, datacenters France. Authentification Pro Santé Connect (e-CPS) pour les professionnels et FranceConnect+ pour les patients.",
  },
  {
    q: "Quelles fonctionnalités sont incluses dans une plateforme MetaConnect ?",
    a: "Téléconsultation (visio chiffrée bout-en-bout, LiveKit, salle d'attente, conforme HAS), téléexpertise asynchrone, télésurveillance avec dispositifs connectés BLE, messagerie MSSanté organisationnelle, consultation DMP (INSi qualifié), agenda multi-praticiens avec prise de RDV patient, fiche vitrine SEO, gestion des utilisateurs (rôles Spatie), comptes-rendus / ordonnances / consentements paramétrables auto-transmis.",
  },
  {
    q: "Puis-je vérifier la disponibilité d'un nom de domaine MSSanté avant de m'inscrire ?",
    a: "Oui — utilisez l'outil de vérification disponible directement sur cette page d'accueil. Nous interrogeons en temps réel la liste blanche officielle de l'ANS pour vous dire si votre sous-domaine `<votre-enseigne>.mssante.fr` est libre.",
  },
  {
    q: "MetaConnect est-il interopérable avec mon logiciel métier ?",
    a: "Oui. Nos briques s'appuient sur les standards nationaux (S/MIME pour MSSanté, FHIR pour les services socles, OAuth2/PSC pour l'identification). Si votre logiciel métier respecte les référentiels ANS, l'intégration est rapide. Sinon, nous proposons un audit technique préalable.",
  },
];

const HOME_SCHEMA = [
  ORG_SCHEMA,
  WEBSITE_SCHEMA,
  softwareApplicationSchema,
  breadcrumb([{ name: "Accueil", url: "/" }]),
  faqSchema(HOME_FAQ),
  MSSANTE_PERSONAS_ITEMLIST,
];

export default function Home() {
  return (
    <div className="min-h-screen" data-testid="page-home">
      <SEO
        title="Logiciel métier e-santé en 5 minutes · MetaConnect Studio"
        description="Le 1er configurateur français de logiciel métier e-santé : aperçu interactif en 5 minutes, marque blanche, hébergement HDS, conforme ANS. Sans engagement."
        canonical="/"
        schema={HOME_SCHEMA}
      />

      {/* ===== HERO — STUDIO FIRST ===== */}
      <section className="relative overflow-hidden pt-32 pb-16 lg:pt-40 lg:pb-20" style={{ background: "#F8FAFC" }} data-testid="section-hero">
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[12px] fade-up"
            style={{
              background: "rgba(79,195,247,0.12)",
              border: "1px solid rgba(79,195,247,0.30)",
              color: "#4FC3F7",
            }}
            data-testid="hero-eyebrow"
          >
            <Sparkles size={13} />
            <span style={{ color: "#4FC3F7", fontWeight: 600 }}>Nouveau · Première mondiale</span>
            <span style={{ color: "rgba(15,43,91,0.40)" }}>·</span>
            <span style={{ color: "#0F2B5B" }}>Configurateur de logiciel métier e-santé</span>
          </div>

          <h1
            className="font-display text-[40px] sm:text-6xl lg:text-[80px] leading-[0.98] tracking-[-0.035em] mt-7 fade-up"
            style={{ color: "#0F2B5B", fontWeight: 600 }}
            data-testid="hero-title"
          >
            Votre{" "}
            <span style={{ color: "#4FC3F7" }}>logiciel métier personnalisé</span>
            <br className="hidden sm:block" />{" "}
            <span style={{ color: "#0F2B5B" }}>en </span>
            <span className="relative inline-block">
              <span style={{ color: "#4FC3F7" }}>5 minutes chrono.</span>
              <span className="absolute -bottom-1 left-0 right-0 h-1 rounded-full" style={{ background: "rgba(79,195,247,0.30)" }} />
            </span>
          </h1>

          <p className="mt-7 mx-auto text-[16px] lg:text-[19px] leading-relaxed max-w-3xl fade-up delay-1" style={{ color: "#64748B" }}>
            <strong style={{ color: "#0F2B5B" }}>MetaConnect Studio</strong> est le <strong style={{ color: "#0F2B5B" }}>premier configurateur français</strong> qui génère une <strong style={{ color: "#0F2B5B" }}>simulation interactive</strong> de votre plateforme de <strong style={{ color: "#0F2B5B" }}>téléconsultation, téléexpertise, télésoin et messagerie MSSanté</strong> à vos couleurs, avec votre logo et vos workflows. À l&apos;issue de la simulation, vous recevez un <strong style={{ color: "#0F2B5B" }}>devis personnalisé</strong> pour la mise en production réelle de votre plateforme. Sans cycle de vente long. Sans engagement.
          </p>

          {/* HeroCounter masque le 16/10/2026 : affichait un chiffre gonfle (+200) et comptait des cahiers des charges, pas des pros. A reactiver uniquement avec une donnee reelle. */}

          {/* PRIMARY CTA — full Studio configurator funnel */}
          <div className="mt-8 flex flex-col items-center gap-3 fade-up delay-1" data-testid="hero-primary-cta">
            <Link
              to="/studio"
              data-testid="hero-cta-studio-primary"
              className="inline-flex items-center gap-2.5 px-9 py-4 rounded-full text-[16px] font-semibold text-white transition hover:brightness-110 shadow-[0_18px_40px_-14px_rgba(15,43,91,0.55)]"
              style={{ background: "#0F2B5B" }}
            >
              Configurer ma plateforme dans le Studio
              <ArrowRight size={18} />
            </Link>
            <p className="text-[12.5px]" style={{ color: "#64748B" }}>
              Exploration libre, sans carte bancaire · un <strong style={{ color: "#0F2B5B" }}>compte gratuit</strong> uniquement pour
              sauvegarder et paramétrer votre interface à 100&nbsp;%
            </p>
          </div>

          {/* Secondary — express preview form */}
          <div className="mt-8 fade-up delay-2" data-testid="hero-secondary-quickstart">
            <div className="flex items-center justify-center gap-3 mb-1">
              <span className="h-px w-16" style={{ background: "#E2E8F0" }} />
              <span className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#94A3B8" }}>ou aperçu express en 30 s</span>
              <span className="h-px w-16" style={{ background: "#E2E8F0" }} />
            </div>
            <QuickstartForm />
          </div>

          <p className="mt-3 text-[12px] fade-up delay-2" style={{ color: "#64748B" }}>
            <Link to="/demo" data-testid="hero-cta-demo" className="underline-offset-2 hover:underline" style={{ color: "#0F2B5B", fontWeight: 500 }}>
              Préférer un échange humain
            </Link>
          </p>

          <p className="mt-4 text-[12.5px] fade-up delay-2" style={{ color: "#64748B" }}>
            ✓ Simulation gratuite · ✓ Cahier des charges PDF inclus · ✓ Devis de mise en production · ✓ Aucune carte bancaire
          </p>

          <div className="mt-7 flex items-center justify-center gap-x-7 gap-y-3 text-[12px] flex-wrap fade-up delay-3" style={{ color: "#64748B" }}>
            <Stat icon={<ShieldCheck size={15} />} label="Hébergement HDS · RGPD" />
            <Stat icon={<Lock size={15} />} label="Pro Santé Connect · FranceConnect+" />
            <Stat icon={<Network size={15} />} label="DMP · MSSanté · INSi · FHIR" />
          </div>

          {/* Ecosystem orbital strip — the "coup de poing" visual */}
          <div className="relative mt-16 lg:mt-20 fade-up delay-3" data-testid="hero-ecosystem">
            <EcosystemStrip />
          </div>

          {/* KPI Wall */}
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 fade-up delay-4" data-testid="hero-kpis">
            <KpiTile value="100%" label="Hébergement HDS · France" />
            <KpiTile value="24-48h" label="Mise en service MSSanté" highlight />
            <KpiTile value="6" label="Briques nationales intégrées" />
            <KpiTile value="ISO 27001" label="Sécurité &amp; conformité" />
          </div>

          {/* Trust marquee — référentiels & cadres de confiance */}
          <div className="mt-14 lg:mt-16 fade-up delay-5" data-testid="trust-marquee">
            <div className="text-[11px] uppercase tracking-[0.18em] mb-5" style={{ color: "#4FC3F7", letterSpacing: "0.08em", fontWeight: 600 }}>
              Référentiels &amp; écosystème de confiance
            </div>
            <TrustMarquee />
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ===== SECTION : STUDIO EN 5 MIN ===== */}
      <section className="relative py-24 lg:py-28 overflow-hidden" data-testid="section-studio">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full opacity-50"
               style={{ background: "radial-gradient(circle, rgba(110,231,197,0.10), transparent 70%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <SectionEyebrow center>Studio · Première mondiale en France</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
              La <span className="text-[#4FC3F7]">matrice de prévisualisation</span> de votre infrastructure de télémédecine.
            </h2>
            <p className="mt-5 text-[15.5px] lg:text-[17px] text-[#64748B] leading-relaxed">
              Pourquoi attendre 3 mois un devis quand vous pouvez voir votre plateforme dans 5 minutes ?
              MetaConnect Studio inverse le cycle de vente classique : <strong className="text-[#0F2B5B]">vous configurez, vous visualisez, vous décidez</strong>.
            </p>
          </div>

          {/* 5-step ladder */}
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
            {[
              { i: Timer,    t: "1 min",  s: "Vos services",       d: "Cochez les briques : téléconsultation, téléexpertise, télésurveillance, messagerie MSS." },
              { i: UserRound, t: "1 min", s: "Votre structure",    d: "Nom, type (cabinet, CPTS, MSP), volume PS, contact." },
              { i: Palette,  t: "1 min",  s: "Votre identité",     d: "Couleurs, police, logo (upload PNG/SVG), domaine ou sous-domaine .metaconnect.fr." },
              { i: Layers,   t: "2 min",  s: "Vos workflows",      d: "Vue Pro vs vue Patient, gestion comptes, modèles CR/ordonnances/consentements." },
              { i: Sparkles, t: "Instant", s: "Votre aperçu live", d: "Lien partageable, PDF CDC, démo navigable avec connexion PSC sandbox. Wow guarantee." },
            ].map((x, i) => (
              <div key={i} className="rounded-2xl border border-[#E2E8F0] bg-white shadow-sm p-5 hover:border-[var(--mc-mint)]/30 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-lg grid place-items-center bg-[rgba(110,231,197,0.10)]">
                    <x.i className="w-4.5 h-4.5 text-[#4FC3F7]" />
                  </div>
                  <span className="text-[10.5px] uppercase tracking-[0.16em] text-[#4FC3F7] font-bold">{x.t}</span>
                </div>
                <div className="text-[14.5px] font-semibold text-[#0F2B5B]">{x.s}</div>
                <p className="mt-1.5 text-[12.5px] text-[#64748B] leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>

          {/* Differentiators */}
          <div className="mt-12 grid lg:grid-cols-3 gap-5">
            <div className="rounded-2xl border border-[#E8EDF5] bg-white p-6">
              <Zap className="w-6 h-6 text-[#4FC3F7]" />
              <div className="mt-3 text-[16px] font-semibold text-[#0F2B5B]">Aucun concurrent en France</div>
              <p className="mt-1.5 text-[13.5px] text-[#64748B] leading-relaxed">Doctolib, Maiia, Maincare, KelDoc : aucun ne permet à un PS de configurer sa propre marque blanche en autonomie. MetaConnect Studio est le seul.</p>
            </div>
            <div className="rounded-2xl border border-[var(--mc-mint)]/30 bg-gradient-to-br from-[rgba(110,231,197,0.10)] to-[rgba(127,179,255,0.05)] p-6">
              <Globe className="w-6 h-6 text-[#4FC3F7]" />
              <div className="mt-3 text-[16px] font-semibold text-[#0F2B5B]">Aperçu = lien partageable</div>
              <p className="mt-1.5 text-[13.5px] text-[#64748B] leading-relaxed">Envoyez le lien à vos associés CPTS, à votre URPS, à vos collaborateurs. Chacun voit la même chose. Décisions accélérées x3.</p>
            </div>
            <div className="rounded-2xl border border-[#E8EDF5] bg-white p-6">
              <ShieldCheck className="w-6 h-6 text-[#4FC3F7]" />
              <div className="mt-3 text-[16px] font-semibold text-[#0F2B5B]">Conformité by design</div>
              <p className="mt-1.5 text-[13.5px] text-[#64748B] leading-relaxed">Hébergement HDS France · Pro Santé Connect · FranceConnect+ · INSi qualifié · DMP · MSSanté · FHIR. Tout est inclus.</p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/studio" data-testid="studio-cta-primary"
                  className="btn-primary px-8 py-4 rounded-full text-[15.5px] font-semibold inline-flex items-center gap-2 shadow-[0_12px_40px_rgba(110,231,197,0.30)]">
              <Wand2 size={17} /> Lancer ma configuration — gratuit · 5 minutes <ArrowRight size={16} />
            </Link>
            <p className="mt-3 text-[12px] text-[#64748B]">Vous repartez avec un PDF de cahier des charges + un lien d&apos;aperçu interactif.</p>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ===== SECTION : NET — ASSISTANT IA SOUVERAIN ===== */}
      <section className="relative py-20 lg:py-24 px-5 lg:px-8" data-testid="section-net">
        <div className="max-w-6xl mx-auto">
          <div
            className="relative overflow-hidden rounded-3xl grid lg:grid-cols-[1fr_320px] gap-8 items-center p-8 lg:p-14"
            style={{ background: "#0F2B5B", boxShadow: "0 24px 60px -24px rgba(15,43,91,0.45)" }}
          >
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl" style={{ background: "rgba(20,184,166,0.18)" }} aria-hidden="true" />
            <div className="relative">
              <div
                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] uppercase tracking-[0.14em] font-bold"
                style={{ background: "rgba(20,184,166,0.15)", border: "1px solid rgba(20,184,166,0.35)", color: "#5EEAD4" }}
              >
                <Sparkles size={12} /> Nouveau · IA souveraine
              </div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl mt-5 leading-tight tracking-[-0.02em]" style={{ color: "#ffffff", fontWeight: 600 }}>
                Rencontrez <span style={{ color: "#5EEAD4" }}>NET</span>, le collaborateur numérique des professionnels de santé
              </h2>
              <p className="mt-4 text-[15px] lg:text-[16px] leading-relaxed max-w-xl" style={{ color: "#B8D4E8" }}>
                Réponses métier instantanées, recherche documentaire, synthèses et préparation de
                messages — propulsé par une IA 100&nbsp;% souveraine (<strong style={{ color: "#ffffff" }}>Mistral</strong>,
                hébergement <strong style={{ color: "#ffffff" }}>HDS</strong>, RGPD) et intégré à OhMyVue.
                <span className="block mt-2 font-medium" style={{ color: "#5EEAD4" }}>« NET assiste. L'humain décide. »</span>
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to="/net"
                  data-testid="home-net-cta"
                  className="inline-flex items-center gap-2 transition hover:-translate-y-0.5"
                  style={{ background: "#14B8A6", color: "#04302B", borderRadius: 999, padding: "12px 26px", fontSize: 14.5, fontWeight: 700, boxShadow: "0 12px 28px -10px rgba(20,184,166,0.5)" }}
                >
                  Découvrir NET <ArrowRight size={16} />
                </Link>
                <Link
                  to="/net/opticien"
                  data-testid="home-net-cta-opticien"
                  className="inline-flex items-center gap-2 transition hover:-translate-y-0.5"
                  style={{ background: "transparent", color: "#B8D4E8", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 999, padding: "11px 24px", fontSize: 14, fontWeight: 600 }}
                >
                  NET pour les opticiens
                </Link>
              </div>
            </div>
            <div className="relative hidden lg:block" data-testid="home-net-mascotte">
              <img
                src="/net-mascotte.png"
                alt="NET, l'assistant IA souverain de MetaConnect"
                className="w-full max-w-[280px] mx-auto drop-shadow-2xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ===== SECTION : VISION (MAISON DU NUMÉRIQUE EN SANTÉ) ===== */}
      <section className="relative hex-soft py-24 lg:py-28" data-testid="section-vision">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <SectionEyebrow>Notre vision</SectionEyebrow>
              <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
                S&apos;inscrire dans la <span className="text-[#0F2B5B]">Maison du Numérique en Santé</span>.
              </h2>
              <p className="mt-5 text-[15px] lg:text-[16px] text-[#64748B] leading-relaxed">
                MetaConnect s&apos;inscrit dans la <strong className="text-[#0F2B5B]">stratégie nationale du numérique
                en santé</strong> en apportant des fondations solides afin de permettre aux professionnels,
                aux établissements et aux éditeurs de bâtir plus rapidement les usages numériques de demain.
              </p>
              <p className="mt-5 text-[13.5px] uppercase tracking-[0.18em] text-[#4FC3F7]">
                Ensemble, construisons l&apos;avenir :
              </p>
              <div className="mt-4 grid sm:grid-cols-1 gap-3" data-testid="vision-targets">
                <VisionTarget icon={<Building2 size={18} />} title="Établissements de santé"
                  desc="Hôpitaux, cliniques, centres de santé qui ont besoin d&apos;interopérabilité pour leurs SI." />
                <VisionTarget icon={<Code2 size={18} />} title="Éditeurs de logiciels"
                  desc="Éditeurs de logiciels médicaux cherchant à intégrer des connecteurs FHIR." />
                <VisionTarget icon={<HeartPulse size={18} />} title="Télémédecine / Télésoin"
                  desc="Solutions de téléconsultation ou téléexpertise nécessitant des flux sécurisés." />
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="mc-card p-4 lg:p-6 overflow-hidden" data-testid="vision-house">
                <img
                  src="https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/aclg74kg_image.png"
                  alt="MetaConnect facilitateur d'interopérabilité — Maison du Numérique en Santé (Délégation au Numérique en Santé)"
                  loading="lazy"
                  className="w-full h-auto rounded-lg"
                />
                <p className="mt-3 text-[11px] italic text-[#64748B] text-center">
                  La Maison du Numérique en Santé — Délégation au Numérique en Santé.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10 rounded-2xl border border-[rgba(110,231,197,0.40)] bg-[rgba(110,231,197,0.06)] px-6 py-5 text-center" data-testid="vision-tagline">
            <p className="font-display text-[#0F2B5B] text-[15px] sm:text-[16.5px] lg:text-[18px] leading-snug">
              Connecter l&apos;existant, accélérer la conformité, fluidifier le parcours.
              <span className="text-[#4FC3F7]"> — Voilà notre contribution.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION : POUR QUI (4 cibles élargies) ===== */}
      <section className="relative py-24 lg:py-28" data-testid="section-audiences">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>Pour qui</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
              Un accompagnement adapté à chaque acteur de la santé numérique.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <AudienceCard
              icon={<Stethoscope size={22} />}
              tone="mint"
              title="Professionnels de santé"
              desc="Accéder à des solutions conformes et interopérables : messagerie MSSanté, DMP, INS, Pro Santé Connect."
              cta={{ to: "/mssante", label: "Voir nos offres" }}
            />
            <AudienceCard
              icon={<Code2 size={22} />}
              tone="blue"
              title="Éditeurs logiciels"
              desc="Intégrer rapidement les services socles nationaux dans vos solutions via nos briques MetaBridge."
              cta={{ to: "/demo", label: "Cadrer une intégration" }}
            />
            <AudienceCard
              icon={<Building2 size={22} />}
              tone="blue"
              title="Établissements &amp; CPTS"
              desc="Faciliter la coordination des parcours de soins entre les acteurs d'un territoire de santé."
              cta={{ to: "/demo", label: "Étudier mon projet" }}
            />
            <AudienceCard
              icon={<GraduationCap size={22} />}
              tone="mint"
              title="Porteurs de projets e-santé"
              desc="Transformer une idée en solution déployable, conforme aux référentiels nationaux."
              cta={{ to: "/demo", label: "Parler à un expert" }}
            />
          </div>
        </div>
      </section>

      {/* ===== SECTION : NOS DOMAINES D'EXPERTISE ===== */}
      <section className="relative hex-soft py-24 lg:py-28" data-testid="section-domains">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>Nos domaines d&apos;expertise</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
              Six domaines au cœur du numérique en santé.
            </h2>
            <p className="mt-5 text-[15px] lg:text-[16px] text-[#64748B] leading-relaxed">
              Une expertise transverse permettant de concevoir, déployer et opérer des solutions à la
              hauteur des enjeux du <strong className="text-[#0F2B5B]">secteur santé français</strong>.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <DomainCard icon={<Network size={20} />} title="Interopérabilité"
                        desc="Standards FHIR, HL7, CDA &middot; alignement Ségur &middot; passerelles services socles ANS." />
            <DomainCard icon={<FileCheck2 size={20} />} title="Conformité"
                        desc="Référentiels ANS, PGSSI-S, MSSanté, INS, Pro Santé Connect, doctrine technique 2026." />
            <DomainCard icon={<Server size={20} />} title="Cybersécurité"
                        desc="HDS, ISO 27001, RGPD, audit, plan d'assurance sécurité, gestion des incidents." />
            <DomainCard icon={<HeartPulse size={20} />} title="Télésanté"
                        desc="Téléconsultation, téléexpertise, télésoin, télésurveillance &middot; conformité ANS." />
            <DomainCard icon={<BrainCircuit size={20} />} title="Intelligence artificielle"
                        desc="IA en santé, dispositifs médicaux, marquage CE, AI Act &middot; cadres éthiques." />
            <DomainCard icon={<Share2 size={20} />} title="Coordination des soins"
                        desc="Parcours patient, équipes pluri-professionnelles, CPTS, ville-hôpital, DAC." />
          </div>
        </div>
      </section>

      {/* ===== SECTION : METABRIDGE (briques d'interopérabilité) ===== */}
      <section id="metabridge" className="relative py-24 lg:py-28" data-testid="section-metabridge">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>MetaBridge</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
              Les briques d&apos;interopérabilité MetaBridge.
            </h2>
            <p className="mt-5 text-[15px] lg:text-[16px] text-[#64748B] leading-relaxed">
              Cinq briques techniques prêtes à être intégrées dans votre logiciel métier ou votre projet
              de santé numérique. Au choix &middot; à la carte &middot; en marque blanche.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <BrickCard icon={<FolderHeart size={20} />} title="MetaBridge DMP"
                       desc="Alimenter et consulter le Dossier Médical Partagé (Mon espace santé) depuis vos outils."
                       to="/services/dmp" />
            <BrickCard icon={<Mail size={20} />} title="MetaBridge MSSanté"
                       desc="Messagerie sécurisée organisationnelle conforme à l'Espace de Confiance ANS."
                       to="/mssante" />
            <BrickCard icon={<Database size={20} />} title="MetaBridge INS"
                       desc="Identifier le patient avec son Identité Nationale de Santé, en conformité ANS."
                       to="/services/ins" />
            <BrickCard icon={<KeyRound size={20} />} title="MetaBridge PSC"
                       desc="Authentification forte des professionnels via Pro Santé Connect (e-CPS, carte CPx)."
                       to="/services/psc" />
            <BrickCard icon={<Plug size={20} />} title="MetaBridge FHIR"
                       desc="Échanger des données de santé structurées avec le standard FHIR R4."
                       to="/services/fhir" />
            <BrickCard icon={<Workflow size={20} />} title="Intégration sur mesure"
                       desc="Vous combinez plusieurs briques ? Cadrons ensemble votre architecture cible."
                       to="/demo" highlight />
          </div>
        </div>
      </section>

      {/* ===== SECTION : EXPERTISE — référentiels & cadres ===== */}
      <section className="relative hex-soft py-24 lg:py-28" data-testid="section-expertise">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>Une expertise reconnue</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
              Une expertise construite autour du numérique en santé.
            </h2>
            <p className="mt-5 text-[15px] lg:text-[16px] text-[#64748B] leading-relaxed">
              MetaConnect intervient au croisement des référentiels nationaux, des standards
              internationaux et des exigences réglementaires propres au secteur santé.
            </p>
          </div>

          <div className="flex flex-wrap gap-2.5">
            {[
              "Référentiels ANS", "MSSanté", "INS", "Mon espace santé",
              "Pro Santé Connect", "RGPD", "HDS", "ISO 27001",
              "PGSSI-S", "Cybersécurité", "Télémédecine", "Télésoin",
              "Ségur du numérique", "FHIR R4", "HL7", "Doctrine technique",
            ].map((tag) => (
              <span key={tag} data-testid={`expertise-tag-${tag}`}
                    className="px-4 py-2 rounded-full text-[12.5px] border border-[#CBD5E0] bg-[#F0F9FF] text-[#475569] hover:border-[rgba(127,179,255,0.45)] transition">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ — SEO RICH SNIPPETS ===== */}
      <section className="relative hex-soft py-24 lg:py-28" data-testid="section-faq">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <SectionEyebrow center>Questions fréquentes</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
              Tout savoir avant de <span className="text-[#4FC3F7]">lancer votre plateforme</span>.
            </h2>
          </div>
          <div className="mt-10 space-y-3">
            {HOME_FAQ.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-[#E8EDF5] bg-white p-5 open:border-[var(--mc-mint)]/40 transition-colors" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-start gap-3">
                  <div className="shrink-0 w-6 h-6 rounded-full grid place-items-center bg-[rgba(110,231,197,0.10)] text-[#4FC3F7] text-[12px] font-bold group-open:rotate-45 transition-transform">+</div>
                  <span className="text-[15px] font-semibold text-[#0F2B5B] leading-snug">{f.q}</span>
                </summary>
                <p className="mt-3 pl-9 text-[14px] text-[#64748B] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA FINALE ===== */}
      <section className="relative py-24 lg:py-32" data-testid="section-final-cta">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <SectionEyebrow center>Passez à l&apos;action — 5 minutes chrono</SectionEyebrow>
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
            Votre plateforme de
            <span className="bg-gradient-to-r from-[#7FB3FF] via-[#4F8FFF] to-[#2D7BFF] bg-clip-text text-transparent">
              {" "}télémédecine
            </span>
            <span className="text-[#0F2B5B]">. </span>
            <span className="text-[#4FC3F7]">Maintenant.</span>
          </h2>
          <p className="mt-6 text-[15.5px] text-[#64748B] max-w-2xl mx-auto">
            Lancez-vous gratuitement — vous repartez avec votre cahier des charges et un lien interactif partageable.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/studio" data-testid="final-cta-studio"
                  className="btn-primary px-7 py-4 rounded-full text-[15.5px] font-semibold inline-flex items-center gap-2 shadow-[0_12px_40px_rgba(110,231,197,0.30)]">
              <Wand2 size={17} /> Configurer ma plateforme maintenant <ArrowRight size={16} />
            </Link>
            <Link to="/demo" data-testid="final-cta-demo"
                  className="btn-ghost px-6 py-4 rounded-full text-[14px] font-medium inline-flex items-center gap-2">
              Demander une démo
            </Link>
            <Link to="/contact" data-testid="final-cta-contact"
                  className="btn-ghost px-6 py-4 rounded-full text-[14px] font-medium inline-flex items-center gap-2">
              Nous contacter
            </Link>
          </div>
          <p className="mt-4 text-[12px] text-[#64748B]">
            ✓ Simulation gratuite · ✓ Cahier des charges PDF · ✓ Devis de mise en production · ✓ Sans CB · ✓ Sans engagement
          </p>
        </div>
      </section>
    </div>
  );
}

/* ===== Sub-components ===== */
function Stat({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-2 text-[#0F2B5B]">
      {icon}<span>{label}</span>
    </span>
  );
}

function SectionEyebrow({ children, center = false }) {
  return (
    <div className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#4FC3F7] ${center ? "" : ""}`}>
      <span className="h-px w-7 bg-[var(--mc-blue-400)]" />
      {children}
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2.5">
      <CheckCircle2 size={15} className="mt-0.5 text-[#4FC3F7] shrink-0" />
      <span>{children}</span>
    </li>
  );
}


function AudienceCard({ icon, tone = "blue", title, desc, cta }) {
  const accent = tone === "mint"
    ? "bg-[rgba(110,231,197,0.12)] border-[rgba(110,231,197,0.40)] text-[#4FC3F7]"
    : "bg-[rgba(45,123,255,0.12)] border-[rgba(127,179,255,0.40)] text-[#0F2B5B]";
  return (
    <div className="mc-card p-6 lg:p-7 flex flex-col" data-testid={`audience-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 28)}`}>
      <div className={`grid place-items-center h-11 w-11 rounded-xl border ${accent}`}>{icon}</div>
      <h3 className="mt-5 font-display text-[#0F2B5B] text-[18.5px] lg:text-[20px] leading-snug">{title}</h3>
      <p className="mt-3 text-[13.5px] text-[#64748B] leading-relaxed flex-1">{desc}</p>
      <div className="mt-5">
        <Link to={cta.to} className="text-[12.5px] text-[#0F2B5B] hover:text-[#0F2B5B] inline-flex items-center gap-1.5">
          {cta.label} <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}

function DomainCard({ icon, title, desc }) {
  return (
    <div className="mc-card p-5 lg:p-6" data-testid={`domain-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 28)}`}>
      <div className="grid place-items-center h-10 w-10 rounded-xl bg-[rgba(45,123,255,0.10)] border border-[rgba(127,179,255,0.30)] text-[#0F2B5B]">{icon}</div>
      <h3 className="mt-4 font-display text-[#0F2B5B] text-[16.5px] leading-snug">{title}</h3>
      <p className="mt-2 text-[13px] text-[#64748B] leading-relaxed">{desc}</p>
    </div>
  );
}

function VisionTarget({ icon, title, desc }) {
  return (
    <div className="rounded-xl border border-[rgba(127,179,255,0.20)] bg-[#F0F9FF] p-4 flex gap-3 items-start hover:border-[rgba(127,179,255,0.40)] transition">
      <div className="grid place-items-center h-9 w-9 rounded-lg bg-[rgba(45,123,255,0.10)] border border-[rgba(127,179,255,0.30)] text-[#0F2B5B] shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="font-display text-[#0F2B5B] text-[14.5px] leading-snug">{title}</div>
        <p className="text-[12px] text-[#64748B] leading-relaxed mt-1">{desc}</p>
      </div>
    </div>
  );
}

function BrickCard({ icon, title, desc, to, highlight = false }) {
  const cls = highlight
    ? "mc-card p-5 lg:p-6 border-[rgba(110,231,197,0.45)] bg-[rgba(110,231,197,0.04)]"
    : "mc-card p-5 lg:p-6";
  return (
    <div className={cls} data-testid={`brick-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 28)}`}>
      <div className={`grid place-items-center h-10 w-10 rounded-xl border ${highlight ? "bg-[rgba(110,231,197,0.12)] border-[rgba(110,231,197,0.40)] text-[#4FC3F7]" : "bg-[rgba(45,123,255,0.10)] border-[rgba(127,179,255,0.30)] text-[#0F2B5B]"}`}>{icon}</div>
      <h3 className="mt-4 font-display text-[#0F2B5B] text-[16.5px] leading-snug">{title}</h3>
      <p className="mt-2 text-[13px] text-[#64748B] leading-relaxed">{desc}</p>
      <div className="mt-4">
        <Link to={to} className={`text-[12.5px] inline-flex items-center gap-1.5 ${highlight ? "text-[#4FC3F7]" : "text-[#0F2B5B]"} hover:text-[#0F2B5B]`}>
          Découvrir <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}


function EcosystemStrip() {
  const nodes = [
    { icon: Mail, label: "MSSanté", tone: "mint" },
    { icon: FolderHeart, label: "DMP", tone: "blue" },
    { icon: KeyRound, label: "INS", tone: "blue" },
    { icon: UserRound, label: "PSC", tone: "blue" },
    { icon: Share2, label: "FHIR", tone: "blue" },
    { icon: Database, label: "Annuaire ANS", tone: "mint" },
  ];
  return (
    <div className="relative">
      {/* connecting glow line behind */}
      <div className="hidden md:block absolute left-0 right-0 top-[44px] h-px glow-line"
           style={{ background: "linear-gradient(90deg, transparent, rgba(127,179,255,0.45) 15%, rgba(110,231,197,0.55) 50%, rgba(127,179,255,0.45) 85%, transparent)" }} />
      <div className="relative grid grid-cols-3 md:grid-cols-6 gap-3 lg:gap-4">
        {nodes.map((n, i) => (
          <div key={n.label}
               className={`fade-up delay-${i + 4} mc-card group relative px-3 py-4 lg:py-5 flex flex-col items-center justify-center gap-2 transition-transform duration-300 hover:-translate-y-1 ${
                 n.tone === "mint"
                   ? "border-[rgba(110,231,197,0.40)] bg-[rgba(110,231,197,0.05)]"
                   : ""
               }`}
               data-testid={`ecosystem-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`grid place-items-center h-10 w-10 rounded-full border ${
              n.tone === "mint"
                ? "bg-[rgba(110,231,197,0.12)] border-[rgba(110,231,197,0.45)] text-[#4FC3F7]"
                : "bg-[rgba(45,123,255,0.10)] border-[rgba(127,179,255,0.30)] text-[#0F2B5B]"
            } group-hover:scale-110 transition-transform duration-300`}>
              <n.icon size={17} />
            </div>
            <div className="text-[12.5px] lg:text-[13px] text-[#0F2B5B] font-medium">{n.label}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 fade-up delay-9 flex justify-center">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.22em] text-[#4FC3F7] border border-[rgba(110,231,197,0.35)] bg-[rgba(110,231,197,0.06)]">
          <Workflow size={12} /> Une seule passerelle &middot; Tous les référentiels nationaux
        </span>
      </div>
    </div>
  );
}

function KpiTile({ value, label, highlight = false }) {
  return (
    <div className={`mc-card px-4 py-5 lg:px-5 lg:py-6 text-left transition-transform duration-300 hover:-translate-y-1 ${
      highlight ? "border-[rgba(110,231,197,0.45)] bg-[rgba(110,231,197,0.05)]" : ""
    }`} data-testid={`kpi-${String(value).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className={`font-display font-semibold leading-none text-3xl lg:text-4xl ${
        highlight ? "text-[#4FC3F7]" : "text-[#0F2B5B]"
      }`}>
        {value}
      </div>
      <div className="mt-2 text-[12px] lg:text-[12.5px] text-[#64748B] leading-snug">{label}</div>
    </div>
  );
}

function TrustMarquee() {
  const logos = [
    {
      label: "MSSanté",
      src: "https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/ja6sd6p6_logo_mssante_home.png",
    },
    {
      label: "Mon espace santé · DMP",
      src: "https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/1wesublw_Logo_Mon_espace_sant%C3%A9.svg.png",
    },
    {
      label: "DMP",
      src: "https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/9d5gcz5k_DMP.png",
    },
    {
      label: "INSi · Identité Nationale de Santé",
      src: "https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/o7ha5tn0_logo_INSi.png",
    },
    {
      label: "Pro Santé Connect",
      src: "https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/lcmw9nyv_ProSanteConnect_COULEURS.png",
    },
    {
      label: "e-CPS",
      src: "https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/3be8cpvr_ecps.png",
    },
    {
      label: "Hébergement HDS",
      src: "https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/8ko9n175_HDS_Shield%281%29.png",
    },
    {
      label: "TLSi",
      src: "https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/90d7jgve_logo-TLSi-generique-01.png",
    },
  ];
  // Duplicate list for seamless loop
  const loop = [...logos, ...logos];
  return (
    <div className="trust-marquee-wrapper relative overflow-hidden rounded-2xl border border-[#E8EDF5] tv-screen py-6"
         style={{ maskImage: "linear-gradient(90deg, transparent 0, #000 6%, #000 94%, transparent 100%)",
                  WebkitMaskImage: "linear-gradient(90deg, transparent 0, #000 6%, #000 94%, transparent 100%)" }}>
      <div className="trust-marquee-track flex items-center gap-5 lg:gap-7 w-max">
        {loop.map((it, i) => (
          <div key={`${it.label}-${i}`}
               title={it.label}
               aria-label={it.label}
               className="shrink-0 h-[72px] lg:h-[84px] min-w-[200px] lg:min-w-[230px] px-6 grid place-items-center rounded-xl bg-white/95 shadow-[0_4px_24px_rgba(0,0,0,0.25)] hover:bg-white transition-colors duration-300"
               data-testid={`trust-logo-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <img
              src={it.src}
              alt={`Logo ${it.label}`}
              loading="lazy"
              className="max-h-[44px] lg:max-h-[52px] w-auto object-contain"
              draggable={false}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function IntegrationVisual() {
  const briques = [
    { icon: Mail, label: "MSSanté", tone: "mint" },
    { icon: Video, label: "Téléconsultation", tone: "blue" },
    { icon: Users, label: "Téléexpertise", tone: "blue" },
    { icon: FolderHeart, label: "DMP", tone: "blue" },
    { icon: Calendar, label: "Agenda", tone: "blue" },
    { icon: UserRound, label: "Espace patient", tone: "blue" },
  ];
  return (
    <div className="mc-card p-7 lg:p-9 relative overflow-hidden" data-testid="integration-visual">
      <div className="absolute inset-0 pointer-events-none opacity-30"
           style={{ background: "radial-gradient(circle at 30% 80%, rgba(45,123,255,0.18), transparent 55%)" }} />
      <div className="relative">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#4FC3F7] mb-4">
          Votre logiciel métier
        </div>
        <div className="rounded-xl border border-[rgba(127,179,255,0.20)] bg-white p-5">
          <div className="text-[13px] text-[#475569] mb-4 inline-flex items-center gap-2">
            <Building2 size={15} className="text-[#4FC3F7]" /> Logiciel cabinet · CRM patient · ERP métier
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#4FC3F7] mb-3">
            + Briques MetaConnect (marque blanche)
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {briques.map((b) => (
              <div key={b.label}
                   className={`rounded-lg border p-2.5 flex items-center gap-2 ${
                     b.tone === "mint"
                       ? "border-[rgba(110,231,197,0.40)] bg-[rgba(110,231,197,0.06)] text-[#059669]"
                       : "border-[rgba(127,179,255,0.28)] bg-[#F0F9FF] text-[#0F2B5B]"
                   }`}>
                <b.icon size={13} />
                <span className="text-[12px]">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-2 text-[10.5px] uppercase tracking-[0.22em] text-[#64748B]">
          <div className="text-center">
            <div className="text-[#4FC3F7] font-mono text-[11px]">REST API</div>
            <div className="mt-1">Intégration</div>
          </div>
          <div className="text-center">
            <div className="text-[#4FC3F7] font-mono text-[11px]">PSC · OAuth2</div>
            <div className="mt-1">Identification</div>
          </div>
          <div className="text-center">
            <div className="text-[#4FC3F7] font-mono text-[11px]">FHIR · S/MIME</div>
            <div className="mt-1">Standards</div>
          </div>
        </div>
      </div>
    </div>
  );
}
